import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Output,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MeetingDto } from '@interfaces/meetings/responses/meeting.response';
import { Option } from '@interfaces/common/option';
import { TranslateService } from '@ngx-translate/core';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { MeetingsHttpService } from '@services/http/meetings/meetings-http.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { first } from 'rxjs';
import {
  mensagemDoErro,
  rotuloDoDealVinculado,
} from '@features/dashboard/meetings/components/meeting-presenter';

/**
 * Desvincular uma ligação de **um** dos deals dela, quando há mais de um (card 3112).
 *
 * Com um só vínculo a tela usa a confirmação simples; aqui a pergunta extra é "de qual?". A
 * própria modal é a confirmação — o texto diz o efeito antes do botão.
 */
@Component({
  selector: 'app-modal-unlink-meeting-deal',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule],
  templateUrl: './modal-unlink-meeting-deal.component.html',
  styleUrl: './modal-unlink-meeting-deal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalUnlinkMeetingDealComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  /** A ligação já sem o vínculo desfeito. */
  @Output() desvinculou = new EventEmitter<MeetingDto>();

  protected readonly ligacao = signal<MeetingDto | null>(null);
  protected readonly opcoes = signal<Option[]>([]);
  protected readonly gravando = signal(false);
  protected dealSelecionado: string | null = null;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    translateService: TranslateService,
    private readonly meetingsService: MeetingsHttpService,
    private readonly toast: ToastHelperService
  ) {
    super(translateService);
  }

  open(meeting: MeetingDto): void {
    this.ligacao.set(meeting);
    this.dealSelecionado = null;
    this.gravando.set(false);
    this.opcoes.set(
      (meeting.deals ?? []).map(link => ({
        label: rotuloDoDealVinculado(link),
        value: link.dealId,
      }))
    );
    this.dialogRef?.open('', '', '', 'Cancelar', '', 'unlink-meeting-deal', false, true);
  }

  isFormValid(): boolean {
    return !!this.dealSelecionado && !this.gravando();
  }

  submitAction(): void {
    const meeting = this.ligacao();
    if (!meeting || !this.dealSelecionado || !this.isFormValid()) return;

    this.gravando.set(true);
    this.meetingsService
      .unlinkDeal(meeting.id, this.dealSelecionado)
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: atualizada => {
          this.gravando.set(false);
          this.toast.showToastSuccess('Reunião desvinculada do deal.');
          if (atualizada) this.desvinculou.emit(atualizada);
          this.fechar();
        },
        error: erro => {
          this.gravando.set(false);
          this.toast.showToastError(
            'Não foi possível desvincular',
            mensagemDoErro(erro, 'Tente de novo em alguns instantes.')
          );
        },
      });
  }

  fechar(): void {
    this.dialogRef?.close();
  }
}
